import { NgClass, UpperCasePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, input, OnInit, output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FlexLayoutModule } from '@angular/flex-layout';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatChipsModule } from '@angular/material/chips';
import { MatDialog } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FavoriteView, Filter } from '@iot-platform/models/common';
import { I4BGridFilterOptions } from '@iot-platform/models/grid-engine';
import { TranslatePipe } from '@ngx-translate/core';
import { get, isEqual } from 'lodash';
import { FilterFieldTriggerComponent } from './components/filter-field-trigger/filter-field-trigger.component';
import { ManageFavoriteFiltersPopupV2Component } from './components/manage-favorite-filters-popup/manage-favorite-filters-popup-v2.component';
import {
  applyFilter,
  getDefaultFavoriteFields,
  getFavoriteFieldsByUserPreferences,
  getFieldLabelFromConfig,
  groupFiltersByFieldAndOperator,
  hasBlankOrNotBlankFilter,
  manageSpecialFiltersFromConfiguration,
  migrateFilters
} from './helpers';

import { FilterCategoryV2, FilterEngineMode, FilterFieldV2, GroupedFilter } from './models';
import { FilterEngineSettingsService } from './services';
import { FilterEngineV2Store } from './state/filter-engine-v2.store';
import { SortUtil } from '@iot-platform/iot-platform-utils';

@Component({
  selector: 'iot-platform-ui-filter-engine-v2',
  standalone: true,
  imports: [
    UpperCasePipe,
    TranslatePipe,
    FlexLayoutModule,
    ReactiveFormsModule,
    FormsModule,
    MatExpansionModule,
    MatFormFieldModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule,
    MatChipsModule,
    MatTooltipModule,
    MatMenuModule,
    MatButtonToggleModule,
    FilterFieldTriggerComponent,
    NgClass
  ],
  providers: [FilterEngineV2Store],
  templateUrl: './filter-engine-v2.component.html',
  styleUrls: ['./filter-engine-v2.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FilterEngineV2Component implements OnInit {
  readonly FilterEngineMode = FilterEngineMode;

  readonly masterView = input.required<string>();
  readonly currentFilters = input<Filter[]>([]);
  readonly expanded = input<boolean>(true);
  readonly suppressOperator = input<boolean>(true);
  readonly maxFilters = input<number>();
  readonly readonly = input<boolean>(false);
  readonly mode = input<FilterEngineMode>(FilterEngineMode.FAVORITE);
  readonly withFavoriteFilters = input<boolean>(false);
  readonly displayManageFavoriteFilters = input<boolean>(true);
  readonly displayActionButtons = input<boolean>(true);
  readonly currentFavoriteView = input<FavoriteView | null>(null);

  readonly applyFilters = output<Filter[]>();

  readonly categoryControl = new FormControl<FilterCategoryV2 | null>(null);
  readonly fieldControl = new FormControl<FilterFieldV2 | null>(null);

  readonly store = inject(FilterEngineV2Store);
  readonly appliedFilters = this.store.appliedFilters;
  readonly visibleFilters = this.store.visibleFilters;
  readonly visibleFiltersCount = this.store.visibleFiltersCount;
  readonly selectedField = this.store.selectedField;
  readonly filterMode = this.store.filterMode;
  readonly filterCriteriaButtonTitle = this.store.filterCriteriaButtonTitle;
  readonly disabled = this.store.disabled;
  readonly configuration = this.store.configuration;
  readonly categories = this.store.categories;
  readonly isLoading = this.store.isLoading;
  readonly multipleOperatorSupport = this.store.multipleOperatorSupport;
  readonly displayResetToFavoriteViewFiltersButton = computed(() => {
    const filters = this.appliedFilters();
    const currentFavoriteView = this.currentFavoriteView();
    return (
      !!filters &&
      !!currentFavoriteView &&
      !isEqual(
        filters.map((f) => ({ criteriaKey: f.criteriaKey, criteriaOperator: f.criteriaOperator, value: f.value })).sort(SortUtil.sortByProperty('criteriaKey')),
        currentFavoriteView?.filters
          ?.map((f) => ({ criteriaKey: f.criteriaKey, criteriaOperator: f.criteriaOperator, value: f.value }))
          ?.sort(SortUtil.sortByProperty('criteriaKey'))
      )
    );
  });
  readonly groupedFilters = computed(() => {
    const appliedFilters = this.visibleFilters();
    const categories = this.categories();
    return groupFiltersByFieldAndOperator(appliedFilters, (criteriaKey) => getFieldLabelFromConfig(criteriaKey, categories));
  });
  maxSelectedFilters = this.store.maxFilters;

  private readonly filterEngineSettingsService = inject(FilterEngineSettingsService);
  readonly userPreferences = this.filterEngineSettingsService.userPreferences;
  readonly favoriteFields = computed(() => {
    const categories = this.categories();
    const userPreferences = this.userPreferences();
    const masterView = this.masterView();
    const displayManageFavoriteFilters = this.displayManageFavoriteFilters();

    if (masterView && userPreferences?.favoriteFilters?.[masterView] && displayManageFavoriteFilters) {
      return getFavoriteFieldsByUserPreferences(categories, userPreferences.favoriteFilters[masterView]);
    }

    return getDefaultFavoriteFields(categories);
  });
  readonly settings = this.filterEngineSettingsService.options;
  private readonly dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    effect(() => this.store.setExpanded(this.expanded()));
    effect(() => this.store.setSuppressOperator(this.suppressOperator()));
    effect(() => this.store.setMaxFilters(this.maxFilters()));
    effect(() => {
      const withFavoriteFilters = this.withFavoriteFilters();
      if (withFavoriteFilters) {
        this.store.setFilterMode(this.mode());
      } else {
        this.store.setFilterMode(FilterEngineMode.CLASSIC);
      }
    });

    effect(() => this.setDisabled());

    effect(() => this.onFilterModeChange());

    effect(() => this.onInitCurrentFilters());

    this.categoryControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((category) => this.store.setSelectedCategory(category));
    this.fieldControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((field) => this.store.setSelectedField(field));
  }

  hasBlankOrNotBlankFilter(operator: I4BGridFilterOptions) {
    return hasBlankOrNotBlankFilter({ criteriaOperator: operator });
  }

  setDisabled() {
    const appliedFilters = this.store.appliedFilters();
    const maxFilters = this.maxSelectedFilters();
    const readonly = this.readonly() || appliedFilters.length >= maxFilters;

    if (readonly) {
      this.store.disable();
    } else {
      this.store.enable();
    }
  }

  onFilterModeChange() {
    const readonly = this.readonly();
    const mode = this.store.filterMode();

    if (readonly) {
      this.categoryControl.disable({ emitEvent: false });
      this.fieldControl.disable({ emitEvent: false });
    } else {
      if (mode === FilterEngineMode.FAVORITE) {
        this.categoryControl.disable({ emitEvent: false });
      } else {
        this.categoryControl.enable({ emitEvent: false });
      }
      this.fieldControl.enable({ emitEvent: false });
    }
  }

  onInitCurrentFilters() {
    const currentFilters = this.currentFilters();

    if (currentFilters.length > 0) {
      const migratedFilters = migrateFilters([...currentFilters], this.categories());
      const fullFilters = manageSpecialFiltersFromConfiguration(this.categories(), migratedFilters);
      this.store.setAppliedFilters([...fullFilters]);
    } else if (currentFilters.length === 0) {
      this.store.clearFilters();
    }
  }

  ngOnInit(): void {
    const masterView = this.masterView();
    if (masterView) {
      this.store.loadConfiguration(masterView);
    }
  }

  openManageFavoriteFilters(): void {
    const masterView = this.masterView();
    const categories = this.categories();
    const userPreferences = this.userPreferences();

    this.dialog
      .open(ManageFavoriteFiltersPopupV2Component, {
        data: { masterView, categories, userPreferences },
        disableClose: true,
        maxWidth: '1100px',
        minWidth: '600px'
      })
      .afterClosed()
      .subscribe((updatedFavoriteFilters) => {
        if (updatedFavoriteFilters) {
          const preferencesToUpdate = {
            ...userPreferences,
            favoriteFilters: {
              ...userPreferences?.favoriteFilters,
              [masterView]: { ...updatedFavoriteFilters }
            }
          };
          this.filterEngineSettingsService.saveUserPreferences(preferencesToUpdate);
        }
      });
  }

  onFilterApplied(event: { filter: Filter; field: FilterFieldV2 }): void {
    const currentFilters = this.store.appliedFilters();
    const result = applyFilter(event.field, event.filter, currentFilters, this.multipleOperatorSupport());

    if (result.applied) {
      const fullFilters = manageSpecialFiltersFromConfiguration(this.categories(), result.filters);
      this.store.setAppliedFilters(fullFilters);
      this.applyFilters.emit(fullFilters);
    }
  }

  removeFilter(filterToRemove: Filter): void {
    const filtersAfterRemove = this.appliedFilters().filter(
      (f) => !(f.criteriaKey === filterToRemove.criteriaKey && f.value === filterToRemove.value && f.criteriaOperator === filterToRemove.criteriaOperator)
    );
    const fullFilters = manageSpecialFiltersFromConfiguration(this.categories(), filtersAfterRemove);
    this.store.setAppliedFilters(fullFilters);
    this.applyFilters.emit(fullFilters);
  }

  resetFilter(group: GroupedFilter): void {
    const currentFilters = this.store.appliedFilters();
    const filteredFilters = currentFilters.filter((f) => !(f.criteriaKey === group.fieldKey && f.criteriaOperator === group.operator));
    this.store.setAppliedFilters(manageSpecialFiltersFromConfiguration(this.categories(), filteredFilters));
    this.applyFilters.emit(filteredFilters);
  }

  clearAllFilters(): void {
    this.store.clearFilters();
    this.applyFilters.emit([]);
  }

  onResetFavoriteView(): void {
    const favoriteView: FavoriteView | null = this.currentFavoriteView();
    const favoriteViewFilters = get(favoriteView, 'filters', []);
    if (Array.isArray(favoriteViewFilters) && favoriteViewFilters.length > 0) {
      const filters: Filter[] = migrateFilters(favoriteViewFilters, this.categories());
      this.store.setAppliedFilters(manageSpecialFiltersFromConfiguration(this.categories(), filters));
      this.applyFilters.emit(filters);
    }
  }

  selectFavoriteField(field: FilterFieldV2): void {
    this.handleFieldSelection(field);
  }

  handleFieldSelection(field: FilterFieldV2): void {
    this.store.setSelectedField(field);
    this.fieldControl.setValue(field);
    this.store.setFilterCriteriaButtonTitle(field?.categoryName as string);
  }

  trackByKey(index: number, item: FilterCategoryV2 | FilterFieldV2): string {
    return item.key || String(index);
  }
}
